import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Sparkle, Rocket } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [tier, setTier] = useState(null);
  const { refreshUser } = useAuth();
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setTier(res.data.tier);
          await refreshUser();
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success">
      <div className="max-w-md w-full glass rounded-3xl p-10 text-center fade-up">
        {status === "polling" && (
          <>
            <span className="w-3 h-3 rounded-full bg-cyan-400 pulse-dot inline-block mb-6" />
            <h1 className="font-display font-bold text-2xl tracking-tight">Confirming your payment…</h1>
            <p className="text-white/50 text-sm mt-3">Hold tight while we activate your access.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="duotone" color="#00D8FF" className="mx-auto" />
            <h1 className="font-display font-black text-3xl tracking-tighter mt-5">You're all set!</h1>
            <p className="text-white/60 mt-3">Your <span className="text-cyan-300 font-semibold capitalize">{tier}</span> access is now active. Enjoy unlimited exploration.</p>
            <Link to="/explore" data-testid="success-explore" className="inline-flex items-center gap-2 mt-8 px-7 py-3 rounded-full font-semibold bg-cyan-400 text-[#030305] glow-cyan hover:bg-cyan-300 transition-colors">
              <Rocket size={18} weight="fill" /> Launch Explorer
            </Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <Sparkle size={48} weight="duotone" color="#A1A1AA" className="mx-auto" />
            <h1 className="font-display font-bold text-2xl tracking-tight mt-5">Almost there</h1>
            <p className="text-white/55 mt-3 text-sm">Your payment is processing. Your access will update shortly — check back in a moment.</p>
            <Link to="/pricing" className="inline-block mt-7 text-cyan-300 text-sm hover:text-cyan-200">Back to Pricing →</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-cancel">
      <div className="max-w-md w-full glass rounded-3xl p-10 text-center fade-up">
        <XCircle size={52} weight="duotone" color="#A1A1AA" className="mx-auto" />
        <h1 className="font-display font-bold text-2xl tracking-tight mt-5">Checkout canceled</h1>
        <p className="text-white/55 mt-3 text-sm">No charge was made. You can upgrade any time.</p>
        <button onClick={() => nav("/pricing")} className="mt-7 px-7 py-3 rounded-full font-semibold border border-white/20 text-white hover:border-cyan-400/60 hover:text-cyan-200 transition-colors">
          Back to Pricing
        </button>
      </div>
    </div>
  );
}
